import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './SystemConfig.css';
import apiService from '../../services/apiService';
import notificationService from '../../CommanService/notificationService';
import { useSelector } from 'react-redux';
import { hasPermission, PERMISSION_CODES } from '../../utils/permissionUtils';
import { apiCallWithCache, clearCacheEntry } from '../../utils/apiUtils';

const SystemConfig = () => {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [companies, setCompanies] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const user = useSelector(state => state.auth.user) || JSON.parse(sessionStorage.getItem('user'));
  const isMountedRef = useRef(true);

  // Permission checks using old method
  const canAccessPage = hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_PAGE);
  const canAdd = hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_ADD);
  const canEdit = hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_EDIT);
  const canDelete = hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_DELETE);
  const canView = hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_VIEW);

  useEffect(() => {
    isMountedRef.current = true;
    GetSystemConfigAndByCompanyID();
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);


  const GetSystemConfigAndByCompanyID = async () => {
    debugger
    if (!isMountedRef.current) return;
    
    console.log('Fetching system config data...');
    setLoading(true);
    
    // Parse user from sessionStorage
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (user) {
      await GetSystemConfig();
    } else if (user && user.company_id) {
      await GetSystemconfigbyid(user.company_id);
    } else {
      if (isMountedRef.current) {
        setTableData([]);
        setLoading(false);
      }
    }
  }

  const GetSystemConfig = async () => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await apiCallWithCache('GetSystemConfig', () => apiService.GetSystemConfig());
      if (isMountedRef.current) {
        // Handle case where response might be null due to cache
        if (response && response.data) {
          console.log('Setting system config data:', response.data);
          setTableData(Array.isArray(response.data) ? response.data : []);
        } else {
          console.log('System config already cached, skipping...');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setTableData([]);
        console.error('GetSystemConfig error:', error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }

  const GetSystemconfigbyid = async (companyId) => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await apiCallWithCache(`GetSystemConfigByID_${companyId}`, () => apiService.GetSystemConfigByID(companyId));
      if (isMountedRef.current) {
        // Handle case where response might be null due to cache
        if (response && response.data) {
          console.log('Setting system config by ID data:', response.data);
          // Handle single object response - convert to array
          if (Array.isArray(response.data)) {
            setTableData(response.data);
          } else if (response.data && typeof response.data === 'object') {
            setTableData([response.data]);
          } else {
            setTableData([]);
          }
        } else {
          console.log('System config by ID already cached, skipping...');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setTableData([]);
        console.error('GetSystemconfigbyid error:', error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }

  // Function to reload system config with cache clearing
  const reloadSystemConfig = async () => {
    debugger
    console.log('Reloading system config...');
    // Clear the cache for system config to force a fresh API call
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (user) {
      clearCacheEntry('GetSystemConfig');
    } else if (user && user.company_id) {
      clearCacheEntry(`GetSystemConfigByID_${user.company_id}`);
    }
    await GetSystemConfigAndByCompanyID();
    console.log('System config reloaded successfully');
  };

  // Filter tableData based on searchTerm
  const filteredData = tableData.filter(row => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      String(row.id).toLowerCase().includes(term) ||
      (row.host_name && row.host_name.toLowerCase().includes(term)) ||
      (row.port && String(row.port).toLowerCase().includes(term)) ||
      (row.user_name && row.user_name.toLowerCase().includes(term)) ||
      (row.db_name && row.db_name.toLowerCase().includes(term)) ||
      (row.company_name && row.company_name.toLowerCase().includes(term)) ||
      (row.sales_data_range && String(row.sales_data_range).toLowerCase().includes(term)) ||
      (row.target_qoh_days && String(row.target_qoh_days).toLowerCase().includes(term))
    );
  });

  // Show only first row of data
  const displayData = filteredData.slice(0, 1);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCompanyChange = (e) => {
    setForm((prev) => ({ ...prev, company_id: e.target.value }));
  };

  const handleModalOpen = async () => {
    setForm({ is_active: true, company_id: '' });
    setIsEditMode(false);
    setShowModal(true);
    try {
      const response = await apiCallWithCache('GetAllCompanies', () => apiService.GetAllCompanies(false));
      if (response && response.data) {
        setCompanies(Array.isArray(response.data) ? response.data : []);
      } else {
        console.log('Companies already cached, skipping...');
      }
    } catch (error) {
      setCompanies([]);
      console.error('Error fetching companies:', error);
    }
  };

  const handleEditButtonClick = async () => {
    if (displayData.length > 0) {
      await handleEdit(displayData[0]);
    }
  };

  const handleModalClose = () => setShowModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedCompany = companies.find(c => c.id === Number(form.company_id));
    const user = JSON.parse(sessionStorage.getItem('user'));
    const userName = user?.name || user?.first_name || user?.username || '';
    
    // Ensure required fields are properly set
    const payload = {
      ...form,
      company_id: form.company_id,
      company_name: selectedCompany?.company_name || selectedCompany?.name,
      created_by_name: userName,
      last_modified_by_name: userName,
      // Ensure these fields are explicitly set for API
      user_name: form.user_name,
      password: form.password
    };
    
    try {
      const response = await apiService.AddAndUpdateSystemConfig(payload);
      setShowModal(false);
      setIsEditMode(false);
      console.log('System config saved successfully, calling reloadSystemConfig...');
      await reloadSystemConfig();
      notificationService.showSuccess(isEditMode ? 'System Config updated successfully!' : 'System Config added successfully!');
    } catch (error) {
      console.error('Error saving system config:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiService.DeleteSystemConfig(id);
      console.log('System config deleted successfully, calling reloadSystemConfig...');
      await reloadSystemConfig();
      notificationService.showSuccess('System Config deleted successfully!');
    } catch (error) {
      console.error('Error deleting system config:', error);
    }
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setViewModal(true);
  };
  const handleViewClose = () => {
    setViewModal(false);
    setSelectedRow(null);
  };

  const handleEdit = async (row) => {
    setForm({ ...row });
    setIsEditMode(true);
    setShowModal(true);
    try {
      const response = await apiCallWithCache('GetAllCompanies', () => apiService.GetAllCompanies(false));
      if (response && response.data) {
        setCompanies(Array.isArray(response.data) ? response.data : []);
      } else {
        console.log('Companies already cached, skipping...');
      }
    } catch (error) {
      setCompanies([]);
      console.error('Error fetching companies:', error);
    }
  };

  return (
    <div className="system-config-table-container">
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.8)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
            Loading System Configuration...
          </div>
          <div className="bouncing-loader">
            <div></div><div></div><div></div>
          </div>
          <style>
            {`
            .bouncing-loader {
              display: flex;
              align-items: flex-end;
              justify-content: center;
              height: 60px;
              gap: 10px;
            }
            .bouncing-loader div {
              width: 18px;
              height: 18px;
              background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
              border-radius: 50%;
              animation: bouncing-loader 0.6s infinite alternate;
              box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
            }
            .bouncing-loader div:nth-child(2) {
              animation-delay: 0.2s;
            }
            .bouncing-loader div:nth-child(3) {
              animation-delay: 0.4s;
            }
            @keyframes bouncing-loader {
              to {
                transform: translateY(-30px) scale(1.1);
                box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
              }
            }
            `}
          </style>
        </div>
      )}
      <div className="system-config-table-header-bar">
        <input className="system-config-search" placeholder="Search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        {canAdd && displayData.length === 0 && <button className="add-system-config-btn" onClick={handleModalOpen}>+ Add System Config</button>}
        {canEdit && displayData.length > 0 && <button className="add-system-config-btn" onClick={handleEditButtonClick}>+ Edit System Config</button>}
      </div>
      
        <table className="system-config-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Host Name</th>
              {/* <th>Port</th> */}
              <th>User Name</th>
              {/* <th>DB Name</th> */}
              <th>Company</th>
              <th>Sales Data Range</th>
              {/* <th>Target QOH Days</th> */}
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center' }}>No data</td></tr>
            ) : (
              displayData.map(row => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.host_name}</td>
                  {/* <td>{row.port}</td> */}
                  <td>{row.user_name}</td>
                  {/* <td>{row.db_name}</td> */}
                  <td>{row.company_name}</td>
                  <td>{row.sales_data_range}</td>
                  {/* <td>{row.target_qoh_days}</td> */}
                  <td>
                  <span className={`status-badge ${row.is_active ? 'active' : 'inactive'}`}>{row.is_active ? 'Active' : 'In Active'}</span>
                </td>
                  <td>
                     {canView && (
                      <span style={{ cursor: 'pointer', marginRight: 8 }} title="View" onClick={() => handleView(row)}>👁️</span>
                    )}
                    {canDelete && user.is_super_admin && (  
                      <span style={{ cursor: 'pointer',  }} title="Delete" onClick={() => handleDelete(row.id)}>🗑️</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
     

      {/* Modal Popup */}
      {showModal && (
        <div className="system-config-modal-backdrop">
          <div className="system-config-modal">
            <button className="system-config-modal-close" onClick={handleModalClose}>&times;</button>
            <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 18 }}>{isEditMode ? 'Edit System Config' : 'Add System Config'}</h2>
            <form className="system-config-modal-form" onSubmit={handleSubmit} autoComplete="off">
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Host Name <span style={{color: 'red'}}>*</span></span>
                <input name="host_name" value={form.host_name} onChange={handleInputChange} required autoComplete="off" />
              </label>
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Port <span style={{color: 'red'}}>*</span></span>
                <input name="port" value={form.port} onChange={handleInputChange} required autoComplete="off" />
              </label>
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>User Name <span style={{color: 'red'}}>*</span></span>
                <input name="user_name" value={form.user_name} onChange={handleInputChange} required autoComplete="off" />
              </label>
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>DB Name <span style={{color: 'red'}}>*</span></span>
                <input name="db_name" value={form.db_name} onChange={handleInputChange} required autoComplete="off" />
              </label>
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Password <span style={{color: 'red'}}>*</span></span>
                <input name="password" value={form.password} onChange={handleInputChange} required type="password" autoComplete="new-password" />
              </label>
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Sales Data Range <span style={{color: 'red'}}>*</span></span>
                <input name="sales_data_range" value={form.sales_data_range} onChange={handleInputChange} required type="number" />
              </label>
              {/* <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Target QOH Days <span style={{color: 'red'}}>*</span></span>
                <input name="target_qoh_days" value={form.target_qoh_days} onChange={handleInputChange} required type="number" />
              </label> */}
              
              <label>
                <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Company Name <span style={{color: 'red'}}>*</span></span>
                <select name="company_id" value={form.company_id} onChange={handleCompanyChange} required>
                  <option value="" disabled>Select Company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.company_name}</option>
                  ))}
                </select>
              </label>
              <label className="system-config-modal-field">
              <span style={{display: 'flex', alignItems: 'center', gap: 2}}>Status</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{form.is_active ? 'Active' : 'In Active'}</span>
                  <div className="switch">
                    <input
                      name="is_active"
                      type="checkbox"
                      checked={!!form.is_active}
                      onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <span className="slider round"></span>
                  </div>
                </div>
              </label>
              <div className="modal-actions">
                <button type="button" onClick={handleModalClose}>Cancel</button>
                <button type="submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewModal && selectedRow && (() => {
        // Configurable columns per row
        const columnsPerRow = 3; // Changed to 3 columns per row as requested
        // Prepare the info fields as an array of {label, value}
        const infoFields = [
          { label: 'ID', value: selectedRow.id },
          { label: 'Company ID', value: selectedRow.company_id },
          { label: 'Company Name', value: selectedRow.company_name },
          { label: 'Created By', value: selectedRow.created_by_name },
          { label: 'Created By (ID)', value: selectedRow.created_by },
          { label: 'Created On', value: selectedRow.created_on ? selectedRow.created_on.split('T')[0] : '-' },
          { label: 'DB Name', value: selectedRow.db_name },
          { label: 'Host Name', value: selectedRow.host_name },
          { label: 'Is Active', value: selectedRow.is_active ? 'True' : 'False' },
          { label: 'Is Deleted', value: selectedRow.is_deleted ? 'True' : 'False' },
          { label: 'Last Modified By', value: selectedRow.last_modified_by_name },
          { label: 'Last Modified By (ID)', value: selectedRow.last_modified_by },
          { label: 'Last Modified On', value: selectedRow.last_modified_on ? selectedRow.last_modified_on.split('T')[0] : '-' },
          { label: 'Password', value: selectedRow.password },
          { label: 'Port', value: selectedRow.port },
          { label: 'Sales Data Range', value: selectedRow.sales_data_range },
          // { label: 'Target QOH Days', value: selectedRow.target_qoh_days },
          { label: 'User Name', value: selectedRow.user_name },
        ];
        // Chunk into rows
        const rows = [];
        for (let i = 0; i < infoFields.length; i += columnsPerRow) {
          rows.push(infoFields.slice(i, i + columnsPerRow));
        }
        // Pad the last row if needed
        const lastRow = rows[rows.length - 1];
        if (lastRow.length < columnsPerRow) {
          for (let i = lastRow.length; i < columnsPerRow; i++) {
            lastRow.push({ label: '', value: '' });
          }
        }
        return (
          <div className="system-config-modal-backdrop">
            <div className="system-config-view-modal">
              <div className="system-config-view-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px 18px 32px', position: 'relative' }}>
                <span className={`status-badge ${selectedRow.is_active ? 'active' : 'inactive'}`}>{selectedRow.is_active ? 'Active' : 'Inactive'}</span>
                <span className="system-config-view-title" style={{ flex: 1, textAlign: 'center', fontSize: '2rem', fontWeight: 700, marginLeft: '-32px' }}>{selectedRow.company_name}</span>
                <button className="system-config-modal-close" onClick={handleViewClose}>&times;</button>
              </div>
              <div className="system-config-view-body">
                <div className="system-config-view-info">
                  {rows.map((row, rowIdx) => (
                    <div className="system-config-view-info-row" style={{ display: 'flex', gap: '24px 32px', marginBottom: 16 }} key={rowIdx}>
                      {row.map((item, colIdx) => (
                        <div key={colIdx} style={{ flex: 1, minWidth: 0, boxSizing: 'border-box' }}>
                          {item.label && <div className="system-config-view-label">{item.label}</div>}
                          <div>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SystemConfig; 